import { PermissionSet, usePermission } from '@context'
import { PreviewHookProps } from '@query/hooks/useRelationSuggestions'
import { DocumentRelationContextInput } from './relationContext'
import { createRelationKey, DocumentRelationFormValue } from './RelationRow'
import { useRelationMapperModal } from './useRelationMapperModal'
import {
  RelationContextForSuggestions,
  useRelationMapperSuggestions
} from './useRelationMapperSuggestions'

export type UseRelationMapperControllerParams = {
  value?: DocumentRelationFormValue[]
  onChange?: (relations: DocumentRelationFormValue[]) => void
  documentId?: number
  readOnly?: boolean
  suggestionsEnabled?: boolean
  canApplySuggestion?: boolean
  previewData?: Omit<PreviewHookProps, 'enabled'>
} & DocumentRelationContextInput

export const useRelationMapperController = ({
  value,
  onChange,
  documentId,
  readOnly = false,
  suggestionsEnabled = true,
  canApplySuggestion = !readOnly,
  previewData,
  sourceDocumentAmount,
  sourceDocumentBalance,
  dueAccountEffect,
  availableLinkKinds: savedAvailableLinkKinds,
  accrualPairDueEffect,
  hasAccrualPairDueMismatch,
  accrualPairInvoiceTotal,
  accrualPairPaymentTotal
}: UseRelationMapperControllerParams) => {
  const hasBookkeepingEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const effectiveReadOnly = readOnly || !hasBookkeepingEditPermission
  const effectiveCanApplySuggestion = canApplySuggestion && hasBookkeepingEditPermission
  const relations = value ?? []
  const handleChange = onChange ?? (() => undefined)

  const relationContextForSuggestions: RelationContextForSuggestions = {
    sourceDocumentAmount,
    sourceDocumentBalance,
    dueAccountEffect,
    availableLinkKinds: savedAvailableLinkKinds,
    accrualPairDueEffect,
    hasAccrualPairDueMismatch,
    accrualPairInvoiceTotal,
    accrualPairPaymentTotal
  }

  const {
    invoiceAmount,
    networkInvoiceTotal,
    networkPaymentTotal,
    liveOpenAmount,
    hasMismatch,
    preferredBalanceSign,
    availableLinkKinds,
    availableLinkKindMetas,
    suggestion,
    shouldShowSuggestion,
    isApplyingSuggestion,
    suggestionApplyLabel,
    handleApplySuggestion
  } = useRelationMapperSuggestions({
    documentId,
    suggestionsEnabled,
    previewData,
    relations,
    handleChange,
    ...relationContextForSuggestions
  })

  const {
    isModalOpen,
    modalLinkKind,
    modalSelection,
    modalInitialSelection,
    setModalSelection,
    handleConfirmSelection,
    handleCloseModal,
    renderQuickActionMenu
  } = useRelationMapperModal({
    relations,
    handleChange,
    effectiveReadOnly,
    availableLinkKinds,
    availableLinkKindMetas
  })

  const handleRemoveRelation = (target: DocumentRelationFormValue) => {
    const targetKey = createRelationKey(target)
    handleChange(relations.filter(relation => createRelationKey(relation) !== targetKey))
  }

  return {
    relations,
    effectiveReadOnly,
    effectiveCanApplySuggestion,
    invoiceAmount,
    networkInvoiceTotal,
    networkPaymentTotal,
    liveOpenAmount,
    hasMismatch,
    preferredBalanceSign,
    suggestion,
    shouldShowSuggestion,
    isApplyingSuggestion,
    suggestionApplyLabel,
    handleApplySuggestion,
    handleRemoveRelation,
    isModalOpen,
    modalLinkKind,
    modalSelection,
    modalInitialSelection,
    availableLinkKindMetas,
    setModalSelection,
    handleConfirmSelection,
    handleCloseModal,
    renderQuickActionMenu
  }
}
